import { Component, OnInit, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AlertService, UIService } from 'src/app/_services';
import { ConfirmationDialogService } from 'src/app/_services/cdialog.service';
import { Tag, TagService, tagTextColor } from 'src/app/_services/tag.service';
import { Project } from 'src/types/Project';

const DEFAULT_COLOR = '#6c757d';

/** A project's tags, with a dialog to create or edit one */
@Component({
    selector: 'app-project-tags',
    templateUrl: './project-tags.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectTagsComponent implements OnInit {
  projectId: string;
  /** the projects-list row passed in navigation state, for the heading */
  project: Project;
  tags: Tag[] = [];
  loading = true;
  msg: string;
  tagTextColor = tagTextColor;

  tagForm: UntypedFormGroup;
  /** the tag being edited; null while the dialog creates one */
  editing: Tag = null;
  submitted = false;
  formErrors: string[] = [];
  @ViewChild('tagDialog') tagDialog: TemplateRef<unknown>;
  private dialog: NgbModalRef;

  constructor(
    private route: ActivatedRoute,
    private tagService: TagService,
    private formBuilder: UntypedFormBuilder,
    private confirmationDialogService: ConfirmationDialogService,
    private alert: AlertService,
    private uiservice: UIService,
    private modalService: NgbModal,
  ) { }

  ngOnInit() {
    this.uiservice.setTitle('Project Tags');
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.project = history.state?.data?.project;
    this.tagForm = this.formBuilder.group({
      name: ['', Validators.required],
      color: [DEFAULT_COLOR],
    });
    this.getTags();
  }

  getTags() {
    this.tagService.list(this.projectId).subscribe({
      next: ({ tags }) => {
        this.tags = tags;
        this.loading = false;
        this.msg = tags.length ? undefined : 'No tags yet';
      },
      error: () => {
        this.loading = false;
        this.msg = 'Something went wrong';
      },
    });
  }

  /** Open the tag dialog, as invenire-v2 does: empty to create a tag, or filled in to edit one */
  openDialog(tag: Tag = null) {
    this.editing = tag;
    this.formErrors = [];
    this.tagForm.reset({ name: tag?.name ?? '', color: tag?.color ?? DEFAULT_COLOR });
    this.dialog = this.modalService.open(this.tagDialog);
  }

  /** Create a tag, or save the one being edited */
  saveTag() {
    this.submitted = true;
    const request = this.editing ?
      this.tagService.update(this.projectId, this.editing.id, this.tagForm.value) :
      this.tagService.create(this.projectId, this.tagForm.value);

    request.subscribe({
      next: () => {
        this.submitted = false;
        this.alert.success(this.editing ? 'Tag updated successfully' : 'Tag created successfully');
        this.dialog.close();
        this.getTags();
      },
      error: ({ error }) => {
        this.submitted = false;
        // a 400 carries the API's validation messages as an array
        this.formErrors = [].concat(error?.message || 'Something went wrong!');
      },
    });
  }

  deleteTag(tag: Tag) {
    this.confirmationDialogService.confirm('Confirmation', `Do you really want to delete tag "${tag.name}"? It is taken off its files. A superadmin can restore it from Deleted Records.`)
      .then(confirmed => {
        if(!confirmed) { return; }
        this.tagService.delete(this.projectId, tag.id).subscribe({
          next: () => {
            this.alert.success('Tag deleted successfully');
            this.getTags();
          },
          error: () => this.alert.error('Something went wrong!'),
        });
      })
      .catch(() => undefined);
  }
}
